// "Identify this part" (Session AP): the one reusable result renderer. Given a reference to a
// photo already stored in V2 (source + id + index), it asks the AI to describe the component and
// shows the four-line best-guess inline, clearly separated, with a disclaimer. READ-ONLY and
// informational: it never writes to any record. Shared by the job-sheet photo gallery and the
// project photo gallery, so there is one identification UI, not two.
import { useCallback, useEffect, useState } from "react";
import { api, type PartIdentificationResult } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";

const DISCLAIMER =
  "This is an AI best-guess identification to inform your next step (an email to a supplier, a note on a quote). It is not a confirmed specification.";

function errorMessage(err: unknown): string {
  return friendlyApiError(err, "Could not identify the part. Please try again.");
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2">
      <div className="font-heading text-xs uppercase tracking-wide text-ele-blue">{label}</div>
      <div className="text-sm text-ele-body">{value.trim() ? value.trim() : <span className="opacity-60">Not provided.</span>}</div>
    </div>
  );
}

export function PartIdentify({
  source,
  id,
  index,
  onClose,
}: {
  source: "jobsheet" | "project";
  id: string;
  index: number;
  onClose?: () => void;
}) {
  const [status, setStatus] = useState<"loading" | "done" | "error">("loading");
  const [result, setResult] = useState<PartIdentificationResult | null>(null);
  const [error, setError] = useState("");

  const run = useCallback(() => {
    setStatus("loading");
    setError("");
    api.partIdentification
      .identify({ source, id, index })
      .then((r) => {
        setResult(r);
        setStatus("done");
      })
      .catch((e) => {
        setError(errorMessage(e));
        setStatus("error");
      });
  }, [source, id, index]);

  useEffect(() => {
    run();
  }, [run]);

  const parsed = result && (result.componentType || result.visibleText || result.observation || result.followUp);

  return (
    <div
      className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-inset p-4"
      role="region"
      aria-label={`Part identification for photo ${index + 1}`}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        {/* text-ele-purple, not the -accent token: inside .office-ui the accent var resolves to a
            near-invisible hairline colour (Session ACG), which is fine for borders, not text. */}
        <h4 className="font-heading text-sm uppercase tracking-wide text-ele-purple">Identify this part: photo {index + 1}</h4>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close identification" className="font-menu text-xs tracking-wide text-ele-body opacity-70 transition hover:opacity-100">
            Close
          </button>
        )}
      </div>

      {status === "loading" && <p className="text-sm text-ele-body opacity-80">Reading the photo...</p>}

      {status === "error" && (
        <div>
          <p className="text-sm text-ele-danger">{error}</p>
          <button
            type="button"
            onClick={run}
            className="mt-2 inline-flex rounded-ele border border-ele-blue px-3 py-1 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset"
          >
            Try again
          </button>
        </div>
      )}

      {status === "done" && result && (
        <div>
          {parsed ? (
            <>
              <Row label="Component type" value={result.componentType} />
              <Row label="Visible text" value={result.visibleText} />
              <Row label="Observation" value={result.observation} />
              <Row label="Follow-up" value={result.followUp} />
            </>
          ) : (
            <div className="mb-2">
              <div className="font-heading text-xs uppercase tracking-wide text-ele-blue">Result</div>
              <p className="whitespace-pre-line text-sm text-ele-body">{result.raw || "No description was returned."}</p>
            </div>
          )}
          <p className="mt-3 border-t border-ele-hairline pt-2 text-xs text-ele-body opacity-70">{DISCLAIMER}</p>
          <button
            type="button"
            onClick={run}
            className="mt-2 inline-flex rounded-ele border border-ele-blue px-3 py-1 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset"
          >
            Identify again
          </button>
        </div>
      )}
    </div>
  );
}
